import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { LISTING_BADGE_LISTING_FIELDS } from '../../config/r2rListingBadges';
import {
  CATEGORY_ID_CORAL,
  CATEGORY_ID_FISH,
  LISTING_TYPE_DRY_GOODS,
} from '../../config/r2rListingTypeCategories';
import { listingCardSponsorHighlightFields } from '../../util/avatarImageQueryFields';
import { LISTING_CARD_MEDIA_FIELDS } from '../../util/listingMedia';
import {
  parseProfileListingsSearch,
  profileListingsSdkParams,
  sanitizeProfileListingsSearch,
} from '../../util/profileListingsQuery';
import { getCoralChildren } from '../../util/profileListingsVisibility';
import { parseProfileTab, PROFILE_TAB_LISTINGS } from '../../util/profilePageTabs';
import { ownProfileManageListingsSearch } from '../../util/ownProfileManageListingsSearch';
import { loadData as loadManageListings } from '../ManageListingsPage/ManageListingsPage.duck';

const { UUID } = sdkTypes;

const DEFAULT_LISTINGS_QUERY = { page: 1, category: null, subcategory: null, sort: null };
const EMPTY_LISTING_TYPE_COUNTS = { corals: 0, fish: 0, dry_goods: 0 };

const totalItemsFromMeta = response => Number(response?.data?.meta?.totalItems ?? 0) || 0;

const queryProfileCount = (sdk, { userId, ownProfileOnly, extra = {} }) => {
  const params = {
    page: 1,
    perPage: 1,
    'fields.listing': ['id'],
    ...extra,
  };
  return ownProfileOnly
    ? sdk.ownListings.query({
        states: ['published'],
        ...params,
      })
    : sdk.listings.query({
        author_id: userId,
        ...params,
      });
};

// ================ Async Thunks ================ //

///////////////
// Show User //
///////////////
const showUserPayloadCreator = ({ userId, config }, { dispatch, rejectWithValue, extra: sdk }) => {
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showUserThunk = createAsyncThunk('ProfilePage/showUser', showUserPayloadCreator);

// Backward compatible wrapper for the thunk
export const showUser = (userId, config) => dispatch => {
  return dispatch(showUserThunk({ userId, config }));
};

/////////////////////////
// Query User Listings //
/////////////////////////
const queryUserListingsPayloadCreator = (
  { userId, config, ownProfileOnly = false, listingsQuery = DEFAULT_LISTINGS_QUERY },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Do not include `author` here. ListingCard on this page uses showAuthorInfo={false},
  // and a sparse `fields.user` author payload would shallow-merge over the full user from
  // showUser — wiping publicData (userType, banner, bio, policies) after first paint.
  const queryParams = {
    include: ['images'],
    'fields.listing': [
      ...new Set([
        'title',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.categoryLevel1',
        'publicData.categoryLevel2',
        'publicData.categoryLevel3',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.cardStyle',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.priceVariationsEnabled',
        'publicData.priceVariants',
        ...listingCardSponsorHighlightFields,
        ...LISTING_BADGE_LISTING_FIELDS,
        ...LISTING_CARD_MEDIA_FIELDS,
      ]),
    ],
    'fields.image': listingCardImageFields(variantPrefix),
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
    ...profileListingsSdkParams(listingsQuery),
  };

  const listingsPromise = ownProfileOnly
    ? sdk.ownListings.query({
        states: ['published'],
        ...queryParams,
      })
    : sdk.listings.query({
        author_id: userId,
        ...queryParams,
      });

  return listingsPromise
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      return { listingRefs, pagination: response.data.meta };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserListingsThunk = createAsyncThunk(
  'ProfilePage/queryUserListings',
  queryUserListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserListings = (
  userId,
  config,
  { ownProfileOnly = false, listingsQuery = DEFAULT_LISTINGS_QUERY } = {}
) => dispatch =>
  dispatch(queryUserListingsThunk({ userId, config, ownProfileOnly, listingsQuery }));

/////////////////////////////////////
// Query published listings count  //
/////////////////////////////////////
const queryPublishedListingsCountPayloadCreator = (
  { userId, ownProfileOnly = false },
  { rejectWithValue, extra: sdk }
) => {
  return queryProfileCount(sdk, { userId, ownProfileOnly })
    .then(response => totalItemsFromMeta(response))
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryPublishedListingsCountThunk = createAsyncThunk(
  'ProfilePage/queryPublishedListingsCount',
  queryPublishedListingsCountPayloadCreator
);

////////////////////////////////
// Query profile listing facets //
////////////////////////////////
const queryProfileListingFacetsPayloadCreator = (
  { userId, config, ownProfileOnly = false },
  { rejectWithValue, extra: sdk }
) => {
  const countParams = { userId, ownProfileOnly };
  return Promise.all([
    queryProfileCount(sdk, { ...countParams, extra: { pub_categoryLevel1: CATEGORY_ID_CORAL } }),
    queryProfileCount(sdk, { ...countParams, extra: { pub_categoryLevel1: CATEGORY_ID_FISH } }),
    queryProfileCount(sdk, { ...countParams, extra: { pub_listingType: LISTING_TYPE_DRY_GOODS } }),
  ])
    .then(([coralsRes, fishRes, dryGoodsRes]) => {
      const typeCounts = {
        corals: totalItemsFromMeta(coralsRes),
        fish: totalItemsFromMeta(fishRes),
        dry_goods: totalItemsFromMeta(dryGoodsRes),
      };

      if (typeCounts.corals <= 0) {
        return { typeCounts, childCounts: {} };
      }

      // Insert keys in hosted coral-child order (not arbitrary Object.keys order).
      const children = getCoralChildren(config.categoryConfiguration);
      return Promise.all(
        children.map(child =>
          queryProfileCount(sdk, {
            ...countParams,
            extra: {
              pub_categoryLevel1: CATEGORY_ID_CORAL,
              pub_categoryLevel2: child.id,
            },
          })
        )
      ).then(childResponses => {
        const childCounts = {};
        children.forEach((child, index) => {
          childCounts[child.id] = totalItemsFromMeta(childResponses[index]);
        });
        return { typeCounts, childCounts };
      });
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryProfileListingFacetsThunk = createAsyncThunk(
  'ProfilePage/queryProfileListingFacets',
  queryProfileListingFacetsPayloadCreator
);

const fetchProfileListings = (dispatch, { userId, config, ownProfileOnly = false, search }) => {
  const parsed = parseProfileListingsSearch(search);

  const headerPromise = dispatch(queryPublishedListingsCountThunk({ userId, ownProfileOnly }))
    .unwrap()
    .catch(() => null);

  const facetsPromise = dispatch(queryProfileListingFacetsThunk({ userId, config, ownProfileOnly }))
    .unwrap()
    .then(payload => ({
      ok: true,
      typeCounts: payload.typeCounts,
      childCounts: payload.childCounts,
    }))
    .catch(() => ({ ok: false }));

  return Promise.all([headerPromise, facetsPromise]).then(([, facets]) => {
    // Only sanitize against counts when those queries succeeded. Empty maps on
    // the failure path would drop a valid deep-link category.
    const sanitized = facets.ok
      ? sanitizeProfileListingsSearch(parsed, {
          typeCounts: facets.typeCounts,
          childCounts: facets.childCounts,
        })
      : parsed;

    return queryListingsAndClampPage(dispatch, {
      userId,
      config,
      ownProfileOnly,
      listingsQuery: sanitized,
    });
  });
};

//////////////////////////
// Query User's Reviews //
//////////////////////////
const queryUserReviewsPayloadCreator = ({ userId }, { rejectWithValue, extra: sdk }) => {
  return sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      return reviews;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryUserReviewsThunk = createAsyncThunk(
  'ProfilePage/queryUserReviews',
  queryUserReviewsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const queryUserReviews = userId => dispatch => {
  return dispatch(queryUserReviewsThunk({ userId }));
};

// ================ Slice ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  pagination: null,
  publishedListingsCount: null,
  listingTypeCounts: EMPTY_LISTING_TYPE_COUNTS,
  coralChildCounts: {},
};

const profilePageSlice = createSlice({
  name: 'ProfilePage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // showUser cases
      .addCase(showUserThunk.pending, (state, action) => {
        state.userShowError = null;
        state.userId = action.meta.arg.userId;
      })
      .addCase(showUserThunk.fulfilled, state => {
        // No state changes needed on success
      })
      .addCase(showUserThunk.rejected, (state, action) => {
        state.userShowError = storableError(action.payload);
      })
      // queryUserListings cases
      .addCase(queryUserListingsThunk.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        // Compare uuid strings. reloadProfileListings builds a new UUID instance
        // each time; object identity would flash an empty grid.
        const sameUser = userId?.uuid === state.userId?.uuid;
        state.userListingRefs = sameUser ? state.userListingRefs : [];
        state.queryListingsError = null;
      })
      .addCase(queryUserListingsThunk.fulfilled, (state, action) => {
        state.userListingRefs = action.payload.listingRefs;
        state.pagination = action.payload.pagination;
      })
      .addCase(queryUserListingsThunk.rejected, (state, action) => {
        state.userListingRefs = [];
        state.pagination = null;
        state.queryListingsError = storableError(action.payload);
      })
      .addCase(queryPublishedListingsCountThunk.fulfilled, (state, action) => {
        state.publishedListingsCount = action.payload;
      })
      .addCase(queryProfileListingFacetsThunk.fulfilled, (state, action) => {
        state.listingTypeCounts = action.payload.typeCounts;
        state.coralChildCounts = action.payload.childCounts;
      })
      // queryUserReviews cases
      .addCase(queryUserReviewsThunk.pending, state => {
        state.queryReviewsError = null;
      })
      .addCase(queryUserReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(queryUserReviewsThunk.rejected, (state, action) => {
        state.reviews = [];
        state.queryReviewsError = action.payload;
      });
  },
});

export const { setInitialState, setUserId } = profilePageSlice.actions;
export default profilePageSlice.reducer;

// ================ Load data ================ //

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

const resolveOwnProfileOnly = (getState, userId, config) => {
  const currentUser = getState()?.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  return (
    isPrivateMarketplace && isAuthorized && hasNoViewingRights && isCurrentUser(userId, currentUser)
  );
};

const queryListingsAndClampPage = (dispatch, { userId, config, ownProfileOnly, listingsQuery }) => {
  return dispatch(queryUserListings(userId, config, { ownProfileOnly, listingsQuery })).then(
    action => {
      if (!queryUserListingsThunk.fulfilled.match(action)) {
        return { query: listingsQuery, pagination: null };
      }
      const pagination = action.payload.pagination;
      if (pagination?.totalPages >= 1 && listingsQuery.page > pagination.totalPages) {
        const clampedQuery = { ...listingsQuery, page: pagination.totalPages };
        return dispatch(
          queryUserListings(userId, config, { ownProfileOnly, listingsQuery: clampedQuery })
        ).then(clamped => ({
          query: clampedQuery,
          pagination: queryUserListingsThunk.fulfilled.match(clamped)
            ? clamped.payload.pagination
            : pagination,
        }));
      }
      return { query: listingsQuery, pagination };
    }
  );
};

// In-app page/filter/sort changes skip loadData (keepScrollAndDataOnSearchChange).
// Re-query the listings grid only — do not wipe user, reviews, or facet counts.
const viewerOwnsProfile = (params, currentUser) =>
  params?.variant !== PROFILE_PAGE_PENDING_APPROVAL_VARIANT &&
  !!currentUser?.id?.uuid &&
  currentUser.id.uuid === params?.id;

const loadOwnerManageListings = (dispatch, getState, { userId, config, search, currentUser }) => {
  const ownProfileOnly = resolveOwnProfileOnly(getState, userId, config);
  return Promise.all([
    dispatch(showUser(userId, config)),
    dispatch(queryUserReviews(userId)),
    dispatch(queryPublishedListingsCountThunk({ userId, ownProfileOnly })),
    dispatch(
      loadManageListings(
        { skipCurrentUserFetch: true, omitStateFilters: true },
        ownProfileManageListingsSearch(search, currentUser),
        config
      )
    ),
  ]);
};

export const reloadProfileListings = (params, search, config) => (dispatch, getState) => {
  const userId = new UUID(params.id);
  const currentUser = getState()?.user?.currentUser;
  if (parseProfileTab(search) === PROFILE_TAB_LISTINGS && viewerOwnsProfile(params, currentUser)) {
    return dispatch(
      loadManageListings(
        { skipCurrentUserFetch: true, omitStateFilters: true },
        ownProfileManageListingsSearch(search, currentUser),
        config
      )
    );
  }
  const ownProfileOnly = resolveOwnProfileOnly(getState, userId, config);
  const parsed = parseProfileListingsSearch(search);
  const { listingTypeCounts, coralChildCounts } = getState().ProfilePage;
  const hasTypeCounts = Object.values(listingTypeCounts || {}).some(n => n > 0);
  const listingsQuery = hasTypeCounts
    ? sanitizeProfileListingsSearch(parsed, {
        typeCounts: listingTypeCounts,
        childCounts: coralChildCounts,
      })
    : parsed;

  return queryListingsAndClampPage(dispatch, { userId, config, ownProfileOnly, listingsQuery });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const currentUser = getState()?.user?.currentUser;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        return Promise.all([
          dispatch(showUser(userId, config)),
          fetchProfileListings(dispatch, { userId, config, search }),
          dispatch(queryUserReviews(userId)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(setUserId(userId));
      } else {
        return Promise.resolve({});
      }
    });
  }

  // Fetch data for plain profile page.
  // Note 1: returns 404s if user is not 'active'.
  // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
  const loadPublicProfile = () => {
    const viewer = getState()?.user?.currentUser;
    const isAuthorized = viewer && isUserAuthorized(viewer);
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
    // On a private marketplace, show active (approved) current user's own page
    // even if they don't have viewing rights
    const canFetchOwnProfileOnly = resolveOwnProfileOnly(getState, userId, config);

    if (!canFetchData) {
      return Promise.resolve();
    } else if (canFetchOwnProfileOnly) {
      return Promise.all([
        dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
        fetchProfileListings(dispatch, {
          userId,
          config,
          ownProfileOnly: canFetchOwnProfileOnly,
          search,
        }),
        dispatch(setUserId(userId)),
      ]);
    }

    return Promise.all([
      dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
      dispatch(showUser(userId, config)),
      fetchProfileListings(dispatch, { userId, config, search }),
      dispatch(queryUserReviews(userId)),
    ]);
  };

  if (parseProfileTab(search) === PROFILE_TAB_LISTINGS) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(fetchedUser => {
      const viewer = fetchedUser || getState()?.user?.currentUser;
      if (viewerOwnsProfile(params, viewer)) {
        return loadOwnerManageListings(dispatch, getState, {
          userId,
          config,
          search,
          currentUser: viewer,
        });
      }
      return loadPublicProfile();
    });
  }

  return loadPublicProfile();
};
